// Une coupure (502, 503, 504) ou une requête restée sans réponse laisse le résultat inconnu : le serveur a pu l'exécuter.
// « Occupé » (409) est refusé avant toute écriture : son refus s'affiche tout de suite.
const RECOVERABLE_ACTIONS = new Set(["move_delivery", "move_delivery_group", "release_delivery_group", "reactivate"]);
const GATEWAY_STATUSES = new Set([502, 503, 504]);
const OPERATION_LABELS = { reactivate: "réactivation", release_delivery_group: "libération" };

export function shouldRecoverPlanningMutation(actionName, response) {
  return RECOVERABLE_ACTIONS.has(actionName)
    && (GATEWAY_STATUSES.has(response?.status) || response?.type === "error");
}

// Requête rejetée par le navigateur (« Failed to fetch ») : remplacée par une réponse d'erreur réseau (Response.error())
// pour suivre la même vérification. Une annulation (AbortError) n'est pas une coupure.
export function resolveUnansweredPlanningRequest(actionName, error) {
  if (RECOVERABLE_ACTIONS.has(actionName) && error instanceof TypeError) return Response.error();
  throw error;
}

export function resolvePlanningResponseCorrelationId(response, payload, fallback = "") {
  return [
    response?.headers?.get?.("X-Correlation-ID"),
    response?.headers?.get?.("X-Request-ID"),
    response?.headers?.get?.("X-Railway-Request-Id"),
    payload?.request_id,
    fallback,
  ].find((value) => typeof value === "string" && value.trim())?.trim() || "";
}

export function findPlanningMutationOutcome(payload, { operationId, actionName, dispatchDate, dispatchPeriod }) {
  if (!operationId || !dispatchDate || !RECOVERABLE_ACTIONS.has(actionName)) return null;
  const latest = payload?.reassignment;
  const entries = [latest, ...(Array.isArray(latest?.history) ? latest.history : [])];
  for (const entry of entries) {
    const diagnostics = entry?.diagnostics;
    if (
      !diagnostics || diagnostics.operation_event_id !== operationId
      || diagnostics.action !== actionName
      || entry.dispatch_date !== dispatchDate || entry.dispatch_period !== dispatchPeriod
    ) continue;
    if (entry.status === "refused" && diagnostics.movement_outcome === "rejected") {
      return { kind: "rejected", diagnostics };
    }
    // Require server persistence evidence, never a client feedback status alone.
    const persisted = actionName === "release_delivery_group"
      ? diagnostics.planning_state_persisted === true
      : diagnostics.atomic_runtime_bundle_persisted === true;
    if (entry.status === "success" && persisted
      && !["rejected", "failed", "cancelled"].includes(diagnostics.movement_outcome)) {
      return { kind: "confirmed", diagnostics };
    }
  }
  return null;
}

export function buildPlanningRecoveryFeedback(kind = "checking", actionName = "move_delivery_group") {
  const operation = OPERATION_LABELS[actionName] || "réaffectation";
  if (kind === "refresh_failed") {
    return {
      title: `${operation[0].toUpperCase()}${operation.slice(1)} enregistrée`,
      message: `Le serveur confirme la ${operation}, mais la vue n'a pas pu être actualisée. La synchronisation du Dispatch reprendra automatiquement.`,
      type: "info", status: "partial", statusLabel: "Vue à actualiser", duration: 12000,
    };
  }
  return {
    title: kind === "checking" ? "Résultat à vérifier" : "Résultat non confirmé",
    message: kind === "checking"
      ? `La réponse a été interrompue. Vérification de la ${operation} auprès du serveur…`
      : `La ${operation} a peut-être été enregistrée. Son résultat n'a pas pu être confirmé. Le Dispatch se synchronisera automatiquement lorsque le serveur sera disponible. Vérifiez les commandes avant de recommencer.`,
    type: kind === "checking" ? "loading" : "info",
    status: kind === "checking" ? "running" : "partial",
    statusLabel: kind === "checking" ? "Vérification en cours" : "Résultat non confirmé",
    duration: kind === "checking" ? null : 12000,
  };
}

async function readJson(url, { fetchImpl, signal, timeoutMs }) {
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (signal?.aborted) abort();
  signal?.addEventListener("abort", abort, { once: true });
  const timer = setTimeout(abort, timeoutMs);
  try {
    const response = await fetchImpl(url, {
      method: "GET", cache: "no-store", headers: { Accept: "application/json" }, signal: controller.signal,
    });
    return response.ok ? await response.json() : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", abort);
  }
}

function waitForProbe(delayMs, signal) {
  return new Promise((resolve) => {
    if (signal?.aborted) return resolve();
    const finish = () => {
      clearTimeout(timer);
      signal?.removeEventListener("abort", finish);
      resolve();
    };
    const timer = setTimeout(finish, delayMs);
    signal?.addEventListener("abort", finish, { once: true });
  });
}

export async function recoverPlanningMutation({
  operationId, actionName, dispatchDate, dispatchPeriod,
  fetchImpl, signal, canContinue = () => true,
  timeoutMs = 30000, pollIntervalMs = 2000, requestTimeoutMs = 5000,
}) {
  const scope = { operationId, actionName, dispatchDate, dispatchPeriod };
  const isActive = () => !signal?.aborted && canContinue();
  const deadline = Date.now() + timeoutMs;
  let outcome = null;
  while (isActive() && Date.now() < deadline) {
    const payload = await readJson("/api/planning/last-diagnostics", {
      fetchImpl, signal, timeoutMs: Math.min(requestTimeoutMs, deadline - Date.now()),
    });
    if (!isActive()) return { kind: "cancelled" };
    outcome = findPlanningMutationOutcome(payload, scope);
    if (outcome) break;
    await waitForProbe(Math.min(pollIntervalMs, Math.max(0, deadline - Date.now())), signal);
  }
  if (!isActive()) return { kind: "cancelled" };
  if (!outcome) return { kind: "unknown" };
  if (outcome.kind === "rejected") {
    return {
      kind: "rejected",
      payload: {
        error: `Le serveur confirme que la ${OPERATION_LABELS[actionName] || "réaffectation"} a été refusée. Vérifiez les contraintes des commandes et du camion.`,
        _planning_diagnostics: outcome.diagnostics,
      },
    };
  }

  const query = new URLSearchParams({ dispatch_date: dispatchDate, dispatch_period: dispatchPeriod, force_fresh: "true" });
  const result = await readJson(`/api/results/latest?${query}`, { fetchImpl, signal, timeoutMs: 20000 });
  if (!isActive()) return { kind: "cancelled" };
  if (!result || !Array.isArray(result.assignments)
    || (result.dispatch_date || result.planning_summary?.dispatch_date) !== dispatchDate
    || (result.dispatch_period || result.planning_summary?.dispatch_period || "day") !== dispatchPeriod) {
    return { kind: "refresh_failed" };
  }
  return {
    kind: "recovered",
    payload: { result_payload: result, _planning_diagnostics: outcome.diagnostics },
  };
}
